import type { useFormat } from '../analytics';
import type { AnalyticsMeta } from '../analytics';
import type { ReportColumn, ReportRow, ReportRowValue } from '../reporting-types';

/**
 * Turns the server's `columns` list into what a table actually draws.
 *
 * `dimensionColumns()` on the server emits up to two entries per dimension — the
 * raw id (`branch`) and, where the fact source can reach one, its label
 * (`branchLabel`). Two columns for one fact reads as a spreadsheet a human did
 * not build, so this merges them back into one display column keyed on the base
 * name; the cell picks the label when there is one and falls back to the id.
 * Measure, comparison, delta and delta% columns pass through unchanged — each
 * is a distinct number and none of them has a pair to merge with.
 */
export interface DisplayColumn {
  key: string;
  label: string;
  kind: ReportColumn['kind'];
  format?: string;
}

export function displayColumns(columns: ReportColumn[]): DisplayColumn[] {
  const seen = new Set<string>();
  const out: DisplayColumn[] = [];
  for (const column of columns) {
    if (column.kind !== 'dimension') {
      out.push(column);
      continue;
    }
    const base = column.key.endsWith('Label') ? column.key.slice(0, -'Label'.length) : column.key;
    if (seen.has(base)) continue;
    seen.add(base);
    out.push({ ...column, key: base });
  }
  return out;
}

/** A merged dimension cell: the label when the source could reach one, else the id. */
export function dimensionValue(row: ReportRow, key: string): ReportRowValue {
  const label = row[`${key}Label`];
  return label ?? row[key] ?? null;
}

/**
 * Renders any report cell by the FORMAT THE SERVER DECLARED for its column —
 * never a client-side guess. `deltaPct` always reads as a signed ratio, since a
 * shrinking measure is exactly the reading a plain `percent()` would flatten.
 */
export function formatCell(
  value: ReportRowValue,
  column: DisplayColumn,
  format: ReturnType<typeof useFormat>,
  weekday: (value: ReportRowValue) => string,
  dimensionMeta: Map<string, AnalyticsMeta['dimensions'][number]>,
): string {
  if (column.kind === 'dimension') {
    const meta = dimensionMeta.get(column.key);
    if (meta?.temporal === 'date') return format.day(value);
    if (meta?.temporal === 'hour') return format.hour(value);
    if (meta?.temporal === 'dayOfWeek') return weekday(value);
    return value === null || value === undefined ? '—' : String(value);
  }

  switch (column.format) {
    case 'money':
      return format.money(value);
    case 'seconds':
      return format.duration(value);
    case 'ratio':
      return format.percent(value, 1);
    case 'decimal':
      return format.number(value, 2);
    case 'integer':
      return format.number(value, 0);
    default:
      return value === null || value === undefined ? '—' : String(value);
  }
}
